import { useEffect, useRef, useState } from 'react'
import type { RefObject } from 'react'

// 채우기 핸들 (선택 영역 오른쪽 아래 작은 네모를 끌어내리기) — 태그 시트·디바이스 시트 공통.
// 그냥 끌면 똑같이 복사, Ctrl 을 누른 채 끌면 자동 증가 (fillSeries), 그동안 커서에 + 표시.
// 그리드 자체 핸들은 편집 중·마지막 행에서 숨어 버려서 끄고 직접 그린다 — 편집 중에도 잡을 수 있고
// (잡는 순간 입력하던 값이 확정된다), 마지막 행 아래로 끌면 행이 늘어난다 (한 번에 200행까지).
// 무엇을 어떻게 채울지는 시트가 정한다 (increments: Ctrl 이면 자동 증가가 되는지, onFill: 실제 채우기).

export type Sel = { min: { col: number; row: number }; max: { col: number; row: number } }
type Cell = { col: number; row: number }

// 채우기 핸들 위에 있거나 끄는 중에, 놓으면 자동 증가가 되는 상태면 body 에 fill-series 를 붙여 커서에 + 를 띄운다.
// 표가 여럿이라 한 번만 설치한다 (마지막 표가 사라지면 해제)
// mode(ctrl) = 지금 놓으면 자동 증가인지 — 핸들을 가진 표가 선택 영역 값을 보고 정한다 (엑셀 규칙, fillSeries)
const fillCursor = { users: 0, dragging: false, overHandle: false, ctrl: false, mode: ((() => false) as (ctrl: boolean) => boolean), uninstall: () => {} }

function syncFillCursor() {
  document.body.classList.toggle('fill-series', (fillCursor.dragging || fillCursor.overHandle) && fillCursor.mode(fillCursor.ctrl))
}

function setFillMode(mode: (ctrl: boolean) => boolean) {
  fillCursor.mode = mode
  syncFillCursor()
}

function startFillDrag(ctrl: boolean) {
  fillCursor.dragging = true
  fillCursor.ctrl = ctrl
  syncFillCursor()
}

function useFillCursor() {
  useEffect(() => {
    if (fillCursor.users++ === 0) {
      const onMove = (e: MouseEvent) => {
        fillCursor.overHandle = (e.target as HTMLElement | null)?.classList?.contains('tag-fill-handle') ?? false
        fillCursor.ctrl = e.ctrlKey || e.metaKey
        syncFillCursor()
      }
      const onKey = (e: KeyboardEvent) => { fillCursor.ctrl = e.ctrlKey || e.metaKey; syncFillCursor() }
      const onUp = () => { fillCursor.dragging = false; syncFillCursor() }
      window.addEventListener('mousemove', onMove, true)
      window.addEventListener('keydown', onKey, true)
      window.addEventListener('keyup', onKey, true)
      window.addEventListener('mouseup', onUp)
      fillCursor.uninstall = () => {
        window.removeEventListener('mousemove', onMove, true)
        window.removeEventListener('keydown', onKey, true)
        window.removeEventListener('keyup', onKey, true)
        window.removeEventListener('mouseup', onUp)
        document.body.classList.remove('fill-series')
      }
    }
    return () => { if (--fillCursor.users === 0) fillCursor.uninstall() }
  }, [])
}

const MAX_EXTRA_ROWS = 200

/**
 * wrapRef = 그리드를 감싼 요소 (position: relative, 핸들·미리보기를 이 안에 그린다).
 * 돌려주는 activeCell/selection 은 그리드의 onActiveCellChange/onSelectionChange 에서, place 는 onScroll 에서 부른다.
 */
export function useFillHandle({ wrapRef, activeCell, rowHeight, increments, onFill }: {
  wrapRef: RefObject<HTMLDivElement | null>
  activeCell: () => Cell | null // 그리드의 지금 활성 칸 (선택 범위가 풀렸을 때 그 칸 하나로)
  rowHeight: number
  increments: (sel: Sel) => (ctrl: boolean) => boolean
  onFill: (sel: Sel, target: number, ctrl: boolean) => void
}) {
  useFillCursor()
  const handleElRef = useRef<HTMLDivElement>(null)
  const [selection, setSelection] = useState<Sel | null>(null)
  const [preview, setPreview] = useState<{ left: number; width: number; top: number; height: number } | null>(null)
  const dragRef = useRef<{ sel: Sel; target: number; rect: DOMRect } | null>(null)

  // 핸들 위치는 그리드가 그린 선택 사각형의 화면 위치를 읽어 DOM 에 바로 넣는다
  const place = () => {
    const wrap = wrapRef.current, handle = handleElRef.current
    if (!wrap || !handle) return
    const rectEl = selection && !preview
      ? wrap.querySelector<HTMLElement>('.dsg-selection-rect') ?? wrap.querySelector<HTMLElement>('.dsg-active-cell')
      : null
    const box = rectEl?.getBoundingClientRect(), base = wrap.getBoundingClientRect()
    // 표 안에서 스크롤해 선택 영역 아래끝이 머리줄 밑이나 표 밖으로 가면 숨긴다
    if (!box || box.bottom < base.top + rowHeight || box.bottom > base.bottom - 2) { handle.style.display = 'none'; return }
    handle.style.display = 'block'
    // 맨 오른쪽 열이면 표 밖으로 반쯤 나가 상자(overflow hidden)에 잘려 잡히지 않는다 — 안쪽으로 붙인다
    handle.style.left = `${Math.min(box.right - base.left - 4, base.width - 9)}px`
    handle.style.top = `${box.bottom - base.top - 4}px`
  }
  const placeRef = useRef(place)
  useEffect(() => { placeRef.current = place; place() })
  // 표 크기가 바뀌거나(창 크기·열 너비) 선택 테두리 전환이 끝난 뒤에도 다시 맞춘다
  useEffect(() => {
    const wrap = wrapRef.current
    if (!wrap) return
    const replace = () => placeRef.current()
    const ro = new ResizeObserver(replace)
    ro.observe(wrap)
    wrap.addEventListener('transitionend', replace)
    return () => { ro.disconnect(); wrap.removeEventListener('transitionend', replace) }
  }, [wrapRef])

  // 행 번호 계산 — 표 안 스크롤만큼 더한다
  const rowAt = (clientY: number) => {
    const wrap = wrapRef.current
    if (!wrap) return 0
    const scrollTop = wrap.querySelector<HTMLElement>('.dsg-container')?.scrollTop ?? 0
    return Math.floor((clientY - wrap.getBoundingClientRect().top - rowHeight + scrollTop) / rowHeight)
  }

  const onMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0 || !selection || !wrapRef.current) return
    e.preventDefault() // 끄는 동안 글자 선택 방지. 그리드는 이 클릭을 '바깥 클릭'으로 보고 편집을 끝낸다 → 입력하던 값 확정
    const rectEl = wrapRef.current.querySelector<HTMLElement>('.dsg-selection-rect') ?? wrapRef.current.querySelector<HTMLElement>('.dsg-active-cell')
    if (!rectEl) return
    dragRef.current = { sel: selection, target: selection.max.row, rect: rectEl.getBoundingClientRect() }
    setFillMode(increments(selection))
    startFillDrag(e.ctrlKey || e.metaKey)

    const base = wrapRef.current.getBoundingClientRect()
    const showPreview = (target: number) => {
      const d = dragRef.current!
      setPreview({
        left: d.rect.left - base.left, width: d.rect.width,
        top: d.rect.top - base.top, height: (target - d.sel.min.row + 1) * rowHeight,
      })
    }
    showPreview(selection.max.row)

    const onMove = (ev: MouseEvent) => {
      const d = dragRef.current
      if (!d) return
      // 아래로만 — 현재 행 수를 넘어가면 그만큼 행을 늘린다
      d.target = Math.min(Math.max(rowAt(ev.clientY), d.sel.max.row), d.sel.max.row + MAX_EXTRA_ROWS)
      showPreview(d.target)
    }
    const onUp = (ev: MouseEvent) => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
      const d = dragRef.current
      dragRef.current = null
      setPreview(null)
      if (d && d.target > d.sel.max.row) onFill(d.sel, d.target, ev.ctrlKey || ev.metaKey)
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }

  return {
    /** 그리드 onActiveCellChange — 선택 범위가 없을 때는 활성 칸 하나가 선택 */
    onActiveCell: (cell: Cell | null, hasRange: boolean) => setSelection(sel => (hasRange ? sel : cell ? { min: cell, max: cell } : null)),
    /** 그리드 onSelectionChange */
    onSelection: (next: Sel | null) => {
      const cell = activeCell()
      setSelection(next ? { min: next.min, max: next.max } : cell ? { min: cell, max: cell } : null)
    },
    /** 그리드 onScroll */
    place: () => placeRef.current(),
    element: (
      <>
        <div ref={handleElRef} onMouseDown={onMouseDown} onMouseEnter={() => { if (selection) setFillMode(increments(selection)) }}
          title="끌면 복사되고, Ctrl을 누른 채 끌면 번호가 늘어납니다"
          className="tag-fill-handle" style={{ display: 'none' }} />
        {preview && <div className="tag-fill-preview" style={preview} />}
      </>
    ),
  }
}
